import React, { useEffect, useState } from 'react';
import { Modal, Input, Form } from 'antd';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface RelayEmailModalProps {
  open: boolean;
  /** Modal title. Defaults to "Email to me". */
  title?: string;
  /** Prefilled address (shop relay_email or the owner's account email). */
  defaultEmail: string;
  /** Send in progress — shows a spinner and keeps the modal open. */
  sending?: boolean;
  /** Called with the (possibly edited) trimmed address. Edits are local only —
   *  they never write the saved relay_email setting. */
  onSend: (address: string) => Promise<void> | void;
  onClose: () => void;
}

/**
 * Small "send this document to myself" prompt. Prefills the relay address and
 * lets the owner override it for this send only, so they can forward/text the
 * link to the customer from their own phone.
 */
const RelayEmailModal: React.FC<RelayEmailModalProps> = ({
  open,
  title = 'Email to me',
  defaultEmail,
  sending,
  onSend,
  onClose,
}) => {
  const [value, setValue] = useState(defaultEmail);

  // Reset to the current default each time the modal opens (or the default
  // resolves after its async fetch).
  useEffect(() => {
    if (open) setValue(defaultEmail);
  }, [open, defaultEmail]);

  const trimmed = value.trim();
  const valid = EMAIL_RE.test(trimmed);

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      okText="Send"
      okButtonProps={{ disabled: !valid, loading: sending }}
      onOk={() => valid && onSend(trimmed)}
    >
      <Form layout="vertical">
        <Form.Item
          label="Send to"
          help="Defaults to your account email. Change it here for this send only — it won't change your saved setting."
          validateStatus={value && !valid ? 'error' : undefined}
        >
          <Input
            type="email"
            value={value}
            placeholder="you@example.com"
            onChange={(e) => setValue(e.target.value)}
            onPressEnter={() => valid && onSend(trimmed)}
            autoFocus
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RelayEmailModal;
